import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, Grid, Stack, TextField, Typography } from '@mui/material'
import Input from 'common/components/form/Input'
import { BasicDialogTitle } from 'common/components/modal/DialogComponents'
import Select from 'common/components/form/Select'
import FileSelection from 'common/components/file/FileSelection'
import { PRODUCT_LIST } from 'common/constants/Constants'
import { aquaFetch } from 'common/utils/ActionUtils'
import ApplicationConf from 'conf/ApplicationConf'

export const DialogAddTicket = ({
    open,
    onClose = () => {},
    onValidate = () => {},
}) => {
    const [id, setId] = useState()
    return (
        <Dialog
            open={open}
            onClose={onClose}
        >
            <BasicDialogTitle onClose={onClose} >Ajouter un ticket à la recette</BasicDialogTitle>
            <DialogContent>
                <Input
                    label='Numéro du ticket'
                    value={id}
                    onChange={setId}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        onValidate(id)
                        setId()
                        onClose()
                    }}
                    variant='contained'
                    color='primary'
                >
                    Valider
                </Button>
            </DialogActions>
        </Dialog>
    )
}

DialogAddTicket.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
}

export const DialogRemoveTicket = ({
    id = 0,
    open,
    onClose = () => {},
    onValidate = () => {},
}) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
        >
            <BasicDialogTitle onClose={onClose} >Enlever un ticket de la recette</BasicDialogTitle>
            <DialogContent>
                <Typography>
                    Voulez vous vraiment enlever le ticket {id.toString()} de la recette ?
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    variant='contained'
                    color='primary'
                >
                    Annuler
                </Button>
                <Button
                    onClick={() => {
                        onValidate(id)
                        onClose()
                    }}
                    variant='contained'
                    color='primary'
                >
                    Valider
                </Button>
            </DialogActions>
        </Dialog>
    )
}

DialogRemoveTicket.propTypes = {
    id: PropTypes.id,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
}

export const DialogOKTicket = ({
    title = 'Passer le ticket en OK',
    ticketName = '',
    open,
    onClose = () => {},
    onValidate = () => {},
    product = 'myliaQ',
    changeLog = '',
    balises = [],
}) => {
    const [changelogLocal, setChangeLog] = useState(changeLog)
    const [productLocal, setProduct] = useState(product)
    // Balises du ticket (set complet éditable) : préremplies avec celles déjà posées.
    // La dialog est montée à neuf à chaque ouverture, donc l'init d'état suffit (pas d'effet sur `open`).
    const [tags, setTags] = useState(balises)
    const [tagOptions, setTagOptions] = useState([])

    useEffect(() => {
        aquaFetch(ApplicationConf.support.tags())
            .then(res => setTagOptions((res ?? []).map(t => t.name)))
    }, [])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='lg'
        >
            <BasicDialogTitle onClose={onClose}>{title}</BasicDialogTitle>
            <DialogContent>
                <Stack spacing={1} sx={{ paddingTop: 1 }}>
                    <Input
                        label='Ticket'
                        value={ticketName}
                        disabled
                    />
                    <Grid container spacing={1}>
                        <Grid size={6}>
                            <Select
                                label='Produit :'
                                value={productLocal}
                                items={PRODUCT_LIST}
                                onChange={setProduct}
                            />
                        </Grid>
                        <Grid size={6}>
                            <Select
                                label='Balises'
                                items={tagOptions.filter(name => !tags.includes(name)).map(name => ({ id: name, label: name }))}
                                onChange={(name) => name && setTags(prev => [...prev, name])}
                                sortItems={false}
                            />
                        </Grid>
                    </Grid>
                    <Box sx={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
                        <Stack direction='row' spacing={1}>
                            {tags.map((tag) => (
                                <Chip
                                    key={tag}
                                    label={tag}
                                    onDelete={() => setTags(prev => prev.filter(t => t !== tag))}
                                    sx={{
                                        color: '#00796b',
                                        backgroundColor: '#e0f7fa',
                                        borderRadius: '10px',
                                        border: 'solid 1px',
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>
                    <TextField
                        label='Changelog :'
                        multiline
                        rows={8}
                        value={changelogLocal}
                        onChange={(e) => setChangeLog(e.target.value)}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    variant='contained'
                    color='primary'
                >
                    Annuler
                </Button>
                <Button
                    onClick={() => {
                        onValidate(changelogLocal, productLocal, tags)
                        setChangeLog('')
                        onClose()
                    }}
                    variant='contained'
                    color='primary'
                    disabled={changelogLocal === ''}
                >
                    Valider
                </Button>
            </DialogActions>
        </Dialog>
    )
}

DialogOKTicket.propTypes = {
    title: PropTypes.string,
    ticketName: PropTypes.string,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    product: PropTypes.string,
    changeLog: PropTypes.string,
    balises: PropTypes.arrayOf(PropTypes.string),
}

export const DialogAddNoteTicket = ({
    title = 'Ajouter une note',
    open,
    onClose = () => {},
    onValidate = () => {},
    message = '',
    files = [],
    showTimeSpent = false,
    timeSpent,
    multiple = false,
    fileAccept = '',
}) => {
    const [messageLocal, setMessage] = useState(message)
    const [filesLocal, setFiles] = useState(files)
    const [timeSpentLocal, setTimeSpent] = useState(timeSpent)

    const close = () => {
        setMessage('')
        setFiles([])
        onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={close}
            maxWidth='lg'
        >
            <BasicDialogTitle onClose={close}>{title}</BasicDialogTitle>
            <DialogContent>
                <Stack spacing={1}>
                    {showTimeSpent && (
                        <Input
                            label='Temps passé (en heures) :'
                            type='number'
                            value={timeSpentLocal}
                            onChange={setTimeSpent}
                        />
                    )}
                    <TextField
                        label='Note :'
                        multiline
                        rows={8}
                        value={messageLocal}
                        onChange={(e) => setMessage(e.target.value)}
                    />
                    <FileSelection
                        files={filesLocal}
                        setFiles={setFiles}
                        multiple={multiple}
                        fileAccept={fileAccept}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={close}
                    variant='contained'
                    color='primary'
                >
                    Annuler
                </Button>
                <Button
                    onClick={() => {
                        onValidate(messageLocal, filesLocal, Number(timeSpentLocal))
                        setMessage('')
                        setFiles([])
                    }}
                    variant='contained'
                    color='primary'
                    disabled={messageLocal === '' || (showTimeSpent && !timeSpentLocal)}
                >
                    Valider
                </Button>
            </DialogActions>
        </Dialog>
    )
}

DialogAddNoteTicket.propTypes = {
    title: PropTypes.string,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    message: PropTypes.string,
    setMessage: PropTypes.func,
    files: PropTypes.string,
    setFiles: PropTypes.func,
    showTimeSpent: PropTypes.bool,
    timeSpent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    multiple: PropTypes.bool,
    fileAccept: PropTypes.string,
}